import { Dispatch, PayloadAction } from "@reduxjs/toolkit";

// Helper function to announce screen reader messages with proper timing and uniqueness
// Preserves zero-width space logic to ensure duplicate messages are announced
const ZERO_WIDTH_SPACE = "\u200B";
const CLEAR_PROCESS_DELAY_MS = 50;
const VOICEOVER_ANNOUNCEMENT_DELAY_MS = 200;
export const STATE_UPDATE_DELAY_MS = 100;
/** Time to allow the user to hear the announcement before clearing the live region */
const ANNOUNCEMENT_CLEAR_DELAY_MS = 3000;
let announcementCounter = 0;
const MAX_COUNTER_VALUE = 1000;

/** Action creator that sets the screen reader announcement (payload: message string) */
export type SetScreenReaderAnnouncementAction = (
    message: string
) => PayloadAction<string>;

/** Action creator that clears the screen reader announcement (no payload) */
export type ClearScreenReaderAnnouncementAction = () => PayloadAction<void>;

/**
 * Announce a message to screen readers via Redux
 * Handles timing delays and ensures uniqueness for duplicate messages
 *
 * To ensure duplicate messages are always announced, we:
 * 1. Clear the message first (forces DOM change)
 * 2. Append zero-width spaces to create unique DOM content
 *
 * The zero-width spaces are invisible to screen readers but ensure each
 * announcement has different DOM content, forcing screen readers to recognize
 * it as a new announcement even when the visible text is identical.
 */
export function announceScreenReaderMessage(
    dispatch: Dispatch,
    setScreenReaderAnnouncementAction: SetScreenReaderAnnouncementAction,
    clearScreenReaderAnnouncementAction: ClearScreenReaderAnnouncementAction,
    message: string
) {
    if (!message) {
        return;
    }

    // Remove any trailing zero-width characters to normalize
    const normalizedMessage = message.replace(/[\u200B\u200C]+$/, "");

    // Increment counter for uniqueness, reset periodically to prevent unbounded growth
    announcementCounter++;
    if (announcementCounter >= MAX_COUNTER_VALUE) {
        announcementCounter = 0;
    }

    // Append a unique pattern of zero-width spaces based on counter
    // Pattern alternates between 1, 2, and 3 zero-width spaces
    const markerCount = (announcementCounter % 3) + 1;
    const messageWithMarker =
        normalizedMessage + ZERO_WIDTH_SPACE.repeat(markerCount);

    // Clear first to force a DOM change
    dispatch(clearScreenReaderAnnouncementAction());

    // Small delay to ensure the clear is processed
    setTimeout(() => {
        // Delay before announcing to give VoiceOver time to finish current speech
        setTimeout(() => {
            dispatch(setScreenReaderAnnouncementAction(messageWithMarker));

            // Clear after user has had time to hear, so the next announcement is clean
            setTimeout(() => {
                dispatch(clearScreenReaderAnnouncementAction());
            }, ANNOUNCEMENT_CLEAR_DELAY_MS);
        }, VOICEOVER_ANNOUNCEMENT_DELAY_MS);
    }, CLEAR_PROCESS_DELAY_MS);
}

/**
 * Announce a message after Redux state update
 * Waits for Redux state update before announcing
 * Use this for answer options, questions, and question groups
 */
export function announceAfterStateUpdate(
    dispatch: Dispatch,
    setScreenReaderAnnouncementAction: SetScreenReaderAnnouncementAction,
    clearScreenReaderAnnouncementAction: ClearScreenReaderAnnouncementAction,
    message: string
) {
    if (!message) {
        return;
    }

    // Wait for state update before announcing
    setTimeout(() => {
        announceScreenReaderMessage(
            dispatch,
            setScreenReaderAnnouncementAction,
            clearScreenReaderAnnouncementAction,
            message
        );
    }, STATE_UPDATE_DELAY_MS);
}
